import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiHome, FiFileText, FiDollarSign, FiTool, FiTarget, FiTrendingUp,FiMapPin } from 'react-icons/fi';
import { propertyAPI, reportAPI } from '../../services/api';
import StatCard from '../../components/shared/StatCard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [availableProperties, setAvailableProperties] = useState([]);  
  
// Dans fetchData, ajouter :  

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, chartRes] = await Promise.all([
          reportAPI.getDashboard(),
          reportAPI.getRevenueChart({ months: 6 }),
        ]);
        const propsRes = await propertyAPI.list({ limit: 6, status: 'disponible' });  
setAvailableProperties(propsRes.data.data || []);
        setStats(dashRes.data);
        setChartData(chartRes.data.map(r => ({
          month: new Date(r.month).toLocaleDateString('fr-FR', { month: 'short' }),
          Revenus: parseFloat(r.income),
          Dépenses: parseFloat(r.expense),
        })));
      } catch (error) {
        console.error('Dashboard error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>Tableau de bord</h1>
      </div>

      <div className="stats-grid">
        <div onClick={() => navigate('/properties')} style={{ cursor: 'pointer' }}>
          <StatCard icon={<FiHome />} label="Biens immobiliers" value={stats?.properties?.total || 0} color="#2563eb" bgColor="#dbeafe" />
        </div>
        <div onClick={() => navigate('/leases')} style={{ cursor: 'pointer' }}>
          <StatCard icon={<FiFileText />} label="Contrats actifs" value={stats?.contracts?.active || 0} color="#22c55e" bgColor="#dcfce7" />
        </div>
        <div onClick={() => navigate('/invoices')} style={{ cursor: 'pointer' }}>
          <StatCard icon={<FiDollarSign />} label="Paiements en attente" value={stats?.pending_payments?.pending || 0} color="#f59e0b" bgColor="#fffbeb" />
        </div>
        <div onClick={() => navigate('/maintenance')} style={{ cursor: 'pointer' }}>
          <StatCard icon={<FiTool />} label="Tickets ouverts" value={stats?.maintenance?.open || 0} color="#ef4444" bgColor="#fef2f2" />
        </div>
        <div onClick={() => navigate('/leads')} style={{ cursor: 'pointer' }}>
          <StatCard icon={<FiTarget />} label="Nouveaux leads" value={stats?.leads?.new || 0} color="#8b5cf6" bgColor="#f3e8ff" />
        </div>
        <StatCard icon={<FiTrendingUp />} label="Revenus du mois" value={`${parseInt(stats?.monthly_revenue?.total || 0).toLocaleString()} XOF`} color="#059669" bgColor="#d1fae5" />
      </div>

      <div className="card">
        <div className="card-header"><h2>Revenus vs Dépenses (6 mois)</h2></div>
        <div className="card-body" style={{ height: 300 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `${parseInt(value).toLocaleString()} XOF`} />
                <Legend />
                <Bar dataKey="Revenus" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Dépenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
              <div className="card" style={{ marginTop: 24 }}>  
  <div className="card-header">  
    <h2>Biens disponibles</h2>  
    <button className="btn btn-secondary btn-sm" onClick={() => navigate('/properties')}>Voir tous</button>  
  </div>  
  <div className="card-body">  
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>  
      {availableProperties.map(p => (  
        <div key={p.id} className="property-card" style={{ cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }} onClick={() => navigate(`/properties/${p.id}`)}>  
          <div style={{ height: 160, background: '#f1f5f9', position: 'relative' }}>  
            {p.image_url ? (  
              <img src={p.image_url} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />  
            ) : (  
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}><FiHome size={32} /></div>  
            )}  
            <span style={{ position: 'absolute', top: 8, right: 8, background: p.transaction_type === 'sale' ? '#2563eb' : '#16a34a', color: 'white', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>  
              {p.transaction_type === 'sale' ? 'Vente' : 'Location'}  
            </span>  
          </div>  
          <div style={{ padding: 12 }}>  
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.title}</div>  
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>  
              <FiMapPin size={12} /> {p.city || '-'}  
            </div>  
            <div style={{ fontWeight: 700, color: 'var(--primary)', marginTop: 8 }}>  
              {p.price ? `${parseInt(p.price).toLocaleString()} XOF` : 'Prix sur demande'}  
              {p.transaction_type === 'rent' && <span style={{ fontWeight: 400, fontSize: 12 }}>/mois</span>}  
            </div>  
          </div>  
        </div>  
      ))}  
    </div>  
    {availableProperties.length === 0 && <div className="empty-state"><p>Aucun bien disponible</p></div>}  
  </div>  
</div>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state"><h3>Aucune donnée de transaction</h3></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
